import React, { useState, useEffect } from "react";
import "./MarkupRules.css";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import Loader from "../../Components/Loader/Loader";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.aeroprime.in/airline-service";
const CRM_BASE_URL = "https://api.aeroprime.in/crm-service";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#1a3c6e",
    color: "#ffffff",
    fontWeight: 600,
  },
}));

const getHeaders = () => ({
  Authorization: localStorage.getItem("AuthToken"),
  Accept: "application/json",
});

const formatDateRange = (from, to) => {
  if (!from && !to) return "-";
  return `${from || "…"} → ${to || "…"}`;
};

const MarkupRules = () => {
  const [rules, setRules] = useState([]);
  const [clientNames, setClientNames] = useState({});
  const [showLoader, setShowLoader] = useState(false);
  const navigate = useNavigate();

  const fetchRules = () => {
    setShowLoader(true);
    axios
      .get(`${BASE_URL}/admin/markupRule/list`, { headers: getHeaders() })
      .then((res) => {
        setRules(Array.isArray(res.data) ? res.data : []);
        setShowLoader(false);
      })
      .catch((err) => {
        setShowLoader(false);
        if (err.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        }
      });
  };

  useEffect(() => {
    fetchRules();
    axios
      .get(`${CRM_BASE_URL}/client/details`, { headers: getHeaders() })
      .then((res) => setClientNames(res.data && typeof res.data === "object" ? res.data : {}))
      .catch(() => setClientNames({}));
  }, []);

  const handleDelete = (ruleId) => {
    if (!window.confirm(`Delete markup rule "${ruleId}"?`)) return;
    setShowLoader(true);
    axios
      .delete(`${BASE_URL}/admin/markupRule/delete/${ruleId}`, { headers: getHeaders() })
      .then((res) => {
        setShowLoader(false);
        if (res.data?.success) {
          fetchRules();
        } else {
          alert(res.data?.message || "Failed to delete rule.");
        }
      })
      .catch(() => {
        setShowLoader(false);
        alert("Failed to delete rule.");
      });
  };

  return (
    <div className="fare-rules-wrapper">
      {showLoader && <Loader hideLoader={false} />}
      <div className="fare-rules-heading">MARKUP RULES</div>
      <div className="Break-line-rp"></div>

      <p className="markup-rules-hint">
        Rules override the base markup set in Adjust Markup. When no rule matches a fare, the
        base markup for that agent and airline is used.
      </p>

      <div className="fare-rules-actions">
        <button className="add-rule-btn" onClick={() => navigate("/addMarkupRule")}>
          + Add Markup Rule
        </button>
      </div>

      <TableContainer component={Paper}>
        <Table className="markup-rules-table" sx={{ minWidth: 1250 }} size="small" aria-label="markup rules table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Rule ID</StyledTableCell>
              <StyledTableCell>Markup</StyledTableCell>
              <StyledTableCell>Client</StyledTableCell>
              <StyledTableCell>Airline</StyledTableCell>
              <StyledTableCell>Priority</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Booking Window</StyledTableCell>
              <StyledTableCell>Travel Window</StyledTableCell>
              <StyledTableCell>Inclusion Criteria</StyledTableCell>
              <StyledTableCell>Exclusion Criteria</StyledTableCell>
              <StyledTableCell>Updated By</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  {showLoader ? "Loading markup rules…" : "No markup rules configured."}
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => (
                <TableRow key={rule.ruleId} hover>
                  <TableCell>{rule.ruleId}</TableCell>
                  <TableCell>
                    {rule.markupType === "FIXED"
                      ? `${rule.markupValue} (fixed)`
                      : `${rule.markupValue}%`}
                  </TableCell>
                  <TableCell>
                    {rule.clientId == null
                      ? "All clients"
                      : clientNames[rule.clientId] || `Client ${rule.clientId}`}
                  </TableCell>
                  <TableCell>{rule.airlineCode || "All airlines"}</TableCell>
                  <TableCell>{rule.priority ?? 0}</TableCell>
                  <TableCell>
                    <span
                      className={`fare-rule-badge ${
                        rule.enabled === false ? "badge-inactive" : "badge-active"
                      }`}
                    >
                      {rule.enabled === false ? "Inactive" : "Active"}
                    </span>
                  </TableCell>
                  <TableCell>{formatDateRange(rule.effectiveFrom, rule.effectiveTo)}</TableCell>
                  <TableCell>{formatDateRange(rule.travelFrom, rule.travelTo)}</TableCell>
                  <TableCell className="json-cell" title={rule.inclusionCriteria}>
                    {rule.inclusionCriteria || "-"}
                  </TableCell>
                  <TableCell className="json-cell" title={rule.exclusionCriteria}>
                    {rule.exclusionCriteria || "-"}
                  </TableCell>
                  <TableCell className="updated-by-cell" title={rule.updatedBy}>{rule.updatedBy || "-"}</TableCell>
                  <TableCell>
                    <div className="action-icons">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/addMarkupRule?ruleId=${rule.ruleId}`)}
                      >
                        <EditIcon fontSize="small" style={{ color: "#334555" }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(rule.ruleId)}>
                        <DeleteIcon fontSize="small" style={{ color: "#ef5443" }} />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default MarkupRules;
